import { makeAutoObservable } from "mobx";
import {
  dateBefore,
  TransactionDateCardModel,
} from "@/shareholderRegister/transactions/commonTransactionsDomain";
import { failure, ok, pending, Result } from "@/common/common";
import { compact, groupBy, identity, round, sum, uniq } from "lodash";
import { CompanyInfo, SuggestedCompany } from "@/types/CompanyInfo";
import {
  MergerCapitalChange,
  MergerDTO,
  MergerShareAllocation,
  MergerType,
  NamedOrganisationNumber,
  TargetShareClassData,
  Transaction,
} from "@/types/Transactions";
import { ExtendedShareholderDTO, ShareholdersInfo } from "@/types/Shareholder";

import { TMessage } from "@/i18n/tt";
import { BooleanFieldModel, NumberFieldModel, RadioFieldModel } from "@/common/commonForms";
import { ShareType, ShareTypeCode, shareTypeLabel } from "@/common/share";
import { newUuid } from "@/utils/uuid";
import { TMessageKey } from "@/i18n/types.gen";
import { UserCompany } from "@/types/UserInfo";
import { combineDateAndTime, toDate } from "@/utils/Dates";
import {
  excludedShareholdersFromActors,
  hasShares,
  excludeExistingShareholders,
} from "@/stores/ShareholdersStore";
import { SelectedActor } from "@/types/SelectActors";
import { toShareholderReference } from "@/utils/UrnReference";
import { filterShareTypesWithShares } from "@/shareholderRegister/transactions/reductionOfNominalValue/reductionOfNominalValueDomain";
import { TransactionStore } from "@/stores/TransactionStore";
import { ApiService } from "@/api";
import { CURRENCY_NOK } from "@/utils/currency";

export enum MergerParty {
  ACQUIROR = "ACQUIROR",
  TARGET = "TARGET",
}

const mergerPartyLabels: { [key in MergerParty]: TMessage } = {
  [MergerParty.ACQUIROR]: { key: "merger.acquiror" },
  [MergerParty.TARGET]: { key: "merger.target" },
};

export const mergerTypeLabels: { [key in MergerType]: TMessage } = {
  [MergerType.PARENT_WHOLLY_OWNED_SUBSIDARY]: { key: "merger.type.PARENT_WHOLLY_OWNED_SUBSIDARY" },
  [MergerType.COMPANIES_WITH_SAME_OWNER]: { key: "merger.type.COMPANIES_WITH_SAME_OWNER" },
  [MergerType.REVERSE_PARENT_SUBSIDIARY]: { key: "merger.type.REVERSE_PARENT_SUBSIDIARY" },
  [MergerType.ORDINARY_ACQUISITION]: { key: "merger.type.ORDINARY_ACQUISITION" },
};

export const mergerInfoLabels: { [key in MergerType]: TMessage } = {
  [MergerType.PARENT_WHOLLY_OWNED_SUBSIDARY]: { key: "merger.info.PARENT_WHOLLY_OWNED_SUBSIDARY" },
  [MergerType.COMPANIES_WITH_SAME_OWNER]: { key: "merger.info.COMPANIES_WITH_SAME_OWNER" },
  [MergerType.REVERSE_PARENT_SUBSIDIARY]: { key: "merger.info.REVERSE_PARENT_SUBSIDIARY" },
  [MergerType.ORDINARY_ACQUISITION]: { key: "merger.info.ORDINARY_ACQUISITION" },
};

export const chooseMergerPartyLabels: { [key in MergerType]: TMessageKey } = {
  [MergerType.PARENT_WHOLLY_OWNED_SUBSIDARY]: "merger.choose-party.PARENT_WHOLLY_OWNED_SUBSIDARY",
  [MergerType.COMPANIES_WITH_SAME_OWNER]: "merger.choose-party.COMPANIES_WITH_SAME_OWNER",
  [MergerType.REVERSE_PARENT_SUBSIDIARY]: "merger.choose-party.REVERSE_PARENT_SUBSIDIARY",
  [MergerType.ORDINARY_ACQUISITION]: "merger.choose-party.ORDINARY_ACQUISITION",
};

export class MergerModel {
  mergerType: RadioFieldModel<MergerType>;
  mergerPartyModel: RadioFieldModel<MergerParty>;
  isDirty = false;
  mergerCapitalChanges: Array<MergerCapitalChangeModel> = [];
  targetShareClassData: Array<TargetShareClassDataModel> = [];
  shareAllocations: Array<MergerShareAllocationModel> = [];
  acquirorNominalValue: NumberFieldModel;
  targetNominalValue: NumberFieldModel;
  registerForTransferringCompany: BooleanFieldModel;
  specifySharePrice: BooleanFieldModel;
  transactionDateCardModel: TransactionDateCardModel;
  searchQuery = "";
  company?: SuggestedCompany;
  shareholderError?: TMessage;
  acquirerError?: TMessage;
  companyError?: TMessage;
  submitResult?: Result<void, TMessage>;
  allocationErrors: TMessage[] = [];
  result?: Result;
  targetShareholdersInfo?: ShareholdersInfo;
  mergerAlreadyExistsOnTarget = false;

  constructor(
    readonly shareholderData: {
      shareTypes: ShareType[];
      shareholders: ExtendedShareholderDTO[];
      shareSumBySharetypeCode: { [shareTypeCode in ShareTypeCode]?: number };
      transactionLog: Transaction[];
      loadShareholdersForDate: (orgNumber: string, date: string) => Generator;
    },
    readonly companyData: {
      orgNumber: string;
      currency: string;
      companyName: string | null;
      companiesWithAdminAccess: UserCompany[];
    },
    readonly transactionStore: TransactionStore,
    readonly apiService: ApiService,
    readonly transactionData?: MergerDTO,
  ) {
    this.mergerType = new RadioFieldModel<MergerType>({
      idFn: identity,
      labelFn: (option) => mergerTypeLabels[option],
      value: transactionData?.mergerType ?? MergerType.ORDINARY_ACQUISITION,
      options: Object.values(MergerType),
      onChange: () => {
        this.validateTargetShareholders();
        this.validateTargetOwnedByAcquiror();
        this.setDirty();
      },
    });
    this.mergerPartyModel = new RadioFieldModel<MergerParty>({
      idFn: identity,
      labelFn: (option) => mergerPartyLabels[option],
      value: transactionData
        ? transactionData.acquiror.orgNo === this.orgNo
          ? MergerParty.ACQUIROR
          : MergerParty.TARGET
        : MergerParty.ACQUIROR,
      options: Object.values(MergerParty),
      onChange: () => {
        this.validateTargetShareholders();
        this.validateTargetOwnedByAcquiror();
        this.setDirty();
      },
    });
    this.acquirorNominalValue = new NumberFieldModel({
      required: true,
      isPositiveOrZero: true,
      value: transactionData?.acquirorNominalValue,
    });
    this.targetNominalValue = new NumberFieldModel({
      required: true,
      isPositiveOrZero: true,
      value: transactionData?.targetNominalValue,
    });
    this.registerForTransferringCompany = new BooleanFieldModel({
      value: true,
      onChange: () => this.setDirty(),
    });
    this.specifySharePrice = new BooleanFieldModel({ value: false });
    this.transactionDateCardModel = new TransactionDateCardModel({
      data: this.shareholderData,
      value: transactionData?.transactionDate,
      onChange: (date, time) => {
        this.setDirty();
        if (date && time) {
          this.fetchState(combineDateAndTime(date, time), transactionData);
        }
      },
    });

    if (transactionData) {
      const counterpart =
        this.mergerPartyModel.value === MergerParty.ACQUIROR
          ? transactionData.target
          : transactionData.acquiror;
      this.company = { name: counterpart.name, organizationnumber: counterpart.orgNo };
      this.searchQuery = `${counterpart.name} (${counterpart.orgNo})`;
    }

    makeAutoObservable(this, {}, { autoBind: true });
  }

  clearDirty() {
    this.isDirty = false;
  }

  get shareTypes() {
    return filterShareTypesWithShares(
      this.shareholderData.shareTypes,
      this.shareholderData.shareSumBySharetypeCode,
    );
  }

  addNewSubscriber(shareholder: ExtendedShareholderDTO): void {
    this.shareTypes.forEach((shareType) => {
      this.shareAllocations.push(new MergerShareAllocationModel(newUuid(), shareholder, shareType));
    });
  }

  addNewSubscribers(shareholders: ExtendedShareholderDTO[]): void {
    shareholders.forEach((shareholder) => {
      this.shareTypes.forEach((shareType) => {
        this.shareAllocations.push(
          new MergerShareAllocationModel(newUuid(), shareholder, shareType),
        );
      });
    });
  }

  get activeAllocations() {
    return this.shareAllocations.filter((a) =>
      this.activeShareTypeIds.includes(a.shareType.sharetypeId),
    );
  }

  get activeShareTypeIds() {
    return this.mergerCapitalChanges.map((c) => c.acquirorShareType.value.sharetypeId);
  }

  get orgNo(): string {
    return this.companyData.orgNumber;
  }

  get companyName(): string {
    return this.companyData.companyName ?? "";
  }

  get currency(): string {
    return this.companyData.currency ?? CURRENCY_NOK;
  }

  *addActorsAsShareholders(actors: SelectedActor[], onSuccess: () => void, onError: () => void) {
    try {
      const shareholders: ExtendedShareholderDTO[] =
        yield this.apiService.addShareholdersFromActors(this.orgNo, actors);
      this.addNewSubscribers(
        excludeExistingShareholders(
          shareholders,
          this.shareAllocations.map((allocation) => allocation.shareholder.id),
        ),
      );
      onSuccess();
    } catch (e) {
      onError();
    }
  }

  get excludedShareholders() {
    return excludedShareholdersFromActors(
      this.shareAllocations.map((allocation) => allocation.shareholder),
    );
  }

  addCapitalChange() {
    this.mergerCapitalChanges.push(
      new MergerCapitalChangeModel(this.shareTypes, this.mergerCapitalChanges),
    );
  }

  removeCapitalChange(id: string) {
    const index = this.mergerCapitalChanges.findIndex((c) => c.id === id);
    this.mergerCapitalChanges.splice(index, 1);
  }

  removeSharesPerShareClass(id: string) {
    const index = this.targetShareClassData.findIndex((c) => c.id === id);
    this.targetShareClassData.splice(index, 1);
  }

  get acquiror(): NamedOrganisationNumber {
    if (this.mergerPartyModel.value === MergerParty.ACQUIROR) {
      return { orgNo: this.orgNo, name: this.companyName };
    } else {
      return { orgNo: this.company?.organizationnumber ?? "", name: this.company?.name ?? "" };
    }
  }

  get target(): NamedOrganisationNumber {
    if (this.mergerPartyModel.value === MergerParty.TARGET) {
      return { orgNo: this.orgNo, name: this.companyName };
    } else {
      return { orgNo: this.company?.organizationnumber ?? "", name: this.company?.name ?? "" };
    }
  }

  get requiresTargetShareClassData() {
    return (
      (this.mergerType.value === MergerType.ORDINARY_ACQUISITION ||
        this.mergerType.value === MergerType.COMPANIES_WITH_SAME_OWNER ||
        this.mergerType.value === MergerType.REVERSE_PARENT_SUBSIDIARY) &&
      this.mergerPartyModel.value === MergerParty.TARGET
    );
  }

  get requiresTargetNominalValue() {
    return (
      (this.mergerType.value === MergerType.REVERSE_PARENT_SUBSIDIARY ||
        this.mergerType.value === MergerType.ORDINARY_ACQUISITION) &&
      this.mergerPartyModel.value === MergerParty.ACQUIROR
    );
  }

  get requiresTargetNumberOfShares() {
    return (
      (this.mergerType.value === MergerType.REVERSE_PARENT_SUBSIDIARY ||
        this.mergerType.value === MergerType.ORDINARY_ACQUISITION) &&
      this.mergerPartyModel.value === MergerParty.ACQUIROR
    );
  }

  get requiresIssuedNumberOfShares() {
    return (
      this.mergerType.value === MergerType.ORDINARY_ACQUISITION &&
      this.mergerPartyModel.value === MergerParty.ACQUIROR
    );
  }

  get requiresPaidInCapital() {
    return (
      (this.mergerType.value === MergerType.ORDINARY_ACQUISITION ||
        this.mergerType.value === MergerType.COMPANIES_WITH_SAME_OWNER ||
        this.mergerType.value === MergerType.REVERSE_PARENT_SUBSIDIARY) &&
      this.mergerPartyModel.value === MergerParty.ACQUIROR
    );
  }

  get requiresShareAllocation() {
    return (
      (this.mergerType.value === MergerType.ORDINARY_ACQUISITION ||
        this.mergerType.value === MergerType.REVERSE_PARENT_SUBSIDIARY) &&
      this.mergerPartyModel.value === MergerParty.ACQUIROR
    );
  }

  get isValid() {
    return (
      this.mergerPartyModel.isValid &&
      this.transactionDateCardModel.isValid &&
      (this.requiresPaidInCapital
        ? this.mergerCapitalChanges.every((change) => change.isValid)
        : true) &&
      (this.requiresTargetShareClassData
        ? this.acquirorNominalValue.isValid &&
          this.targetShareClassData.every((numberOfShares) => numberOfShares.isValid)
        : true) &&
      (this.requiresTargetNominalValue
        ? this.targetNominalValue.isValid && this.allocationErrors.length === 0
        : true) &&
      (this.requiresShareAllocation ? this.allocationErrors.length === 0 : true) &&
      this.companyError === undefined &&
      this.shareholderError === undefined &&
      this.acquirerError === undefined
    );
  }

  validate() {
    this.companyError = this.company ? undefined : { key: "merger-company-is-required" };
    this.mergerPartyModel.validate();
    this.mergerCapitalChanges.forEach((change) => change.validate());
    this.transactionDateCardModel.validate();
    this.targetShareClassData.forEach((numberOfShares) => numberOfShares.validate());
    this.acquirorNominalValue.validate();
    this.targetNominalValue.validate();

    this.allocationErrors = [];
    if (this.requiresShareAllocation) {
      const allocationsByShareType = groupBy(this.activeAllocations, "shareType.shareTypeCode");
      Object.keys(allocationsByShareType).forEach((shareTypeCode) => {
        const allocatedCount = sum(
          allocationsByShareType[shareTypeCode].map((a) => a.numberOfShares.parsedValue ?? 0),
        );
        const change = this.mergerCapitalChanges.find(
          (c) => c.acquirorShareType.value.shareTypeCode === shareTypeCode,
        );
        const totalCount =
          (this.mergerType.value === MergerType.ORDINARY_ACQUISITION
            ? change?.issuedNumberOfShares.parsedValue
            : this.shareholderData.shareSumBySharetypeCode[shareTypeCode as ShareTypeCode]) ?? 0;
        if (allocatedCount !== totalCount) {
          const shareType = this.activeAllocations.find(
            (a) => a.shareType.shareTypeCode === shareTypeCode,
          )!.shareType;
          this.allocationErrors.push({
            key: "merger.allocation-error",
            params: {
              shareType: { type: "shareType", value: shareType },
              allocatedCount,
              totalCount,
            },
          });
        }
      });
    }

    return this.isValid;
  }

  setDirty() {
    this.isDirty = true;
  }

  *initEditMerger(transaction: Transaction) {
    const transactionData = transaction.payload as MergerDTO;
    yield this.fetchState(transaction.transactionDate, transactionData);

    this.mergerCapitalChanges = [];
    transactionData.mergerCapitalChanges.forEach((change) => {
      const acquirorShareType = this.shareTypes.find(
        (st) => st.sharetypeId === change.acquirorShareTypeId,
      );
      const model = new MergerCapitalChangeModel(
        this.shareTypes,
        this.mergerCapitalChanges,
        change.targetShareTypeCode,
        acquirorShareType,
        undefined,
        change,
      );
      this.mergerCapitalChanges.push(model);
    });

    this.shareAllocations = compact(
      transactionData.shareAllocation.map((allocation) => {
        const shareholder = this.shareholderData.shareholders.find(
          (s) => s.id === allocation.shareholderId,
        );
        const shareType = this.shareTypes.find((st) => st.sharetypeId === allocation.shareTypeId);
        if (!shareholder || !shareType) return undefined;
        return new MergerShareAllocationModel(
          newUuid(),
          shareholder,
          shareType,
          allocation.numberOfShares,
          {
            sharePrice: allocation.sharePrice,
            sharePremium: allocation.sharePremium,
            paidInShareCapital: allocation.paidInShareCapital,
            bonusShareCapital: allocation.bonusShareCapital,
          },
        );
      }),
    );
    const hasSharePriceData = transactionData.shareAllocation.some(
      (a) =>
        a.sharePrice !== undefined ||
        a.sharePremium !== undefined ||
        a.paidInShareCapital !== undefined ||
        a.bonusShareCapital !== undefined,
    );
    if (hasSharePriceData) {
      this.specifySharePrice.setValue(true);
    }
  }

  *fetchState(dateTime: string, transactionData?: MergerDTO) {
    yield this.shareholderData.loadShareholdersForDate(this.orgNo, dateBefore(dateTime));

    this.targetShareClassData = this.shareTypes.map(
      (shareType) =>
        new TargetShareClassDataModel(
          shareType,
          transactionData?.targetShareClassData?.find(
            (data) => data.targetShareTypeId === shareType.sharetypeId,
          ),
        ),
    );

    if (!transactionData && this.company && this.canCopyTargetShareholders) {
      yield this.fetchTargetShareholders(this.company.organizationnumber, dateTime);
    }
    yield this.refreshMergerExistsOnTarget();
  }

  setSearchQuery(query: string) {
    this.searchQuery = query;
    this.setDirty();
  }

  *selectCompany(company?: SuggestedCompany) {
    this.company = company;
    yield this.refreshMergerExistsOnTarget();
    yield this.validateTargetOwnedByAcquiror();
    if (this.company && this.canCopyTargetShareholders) {
      yield this.fetchTargetNominalValue(this.company.organizationnumber);
      yield this.fetchTargetShareholders(
        this.company.organizationnumber,
        this.transactionDateCardModel.combinedDateAndTime!,
      );
    } else {
      this.targetNominalValue.setInputValue(undefined);
      this.mergerCapitalChanges = [];
      this.shareTypes.forEach((shareType) => {
        this.mergerCapitalChanges.push(
          new MergerCapitalChangeModel(
            this.shareTypes,
            this.mergerCapitalChanges,
            undefined,
            shareType,
          ),
        );
      });
      this.shareAllocations = [];
    }
  }

  get isAdminInAcquiringCompany() {
    return (
      this.mergerPartyModel.value === MergerParty.TARGET &&
      this.companyData.companiesWithAdminAccess.some(
        (c) => c.organisationNumber === this.company?.organizationnumber,
      )
    );
  }

  get canCopyTargetShareholders() {
    return (
      this.company &&
      this.requiresShareAllocation &&
      this.companyData.companiesWithAdminAccess.some(
        (c) => c.organisationNumber === this.company?.organizationnumber,
      )
    );
  }

  get targetShareholderIdsToCopy(): number[] {
    if (!this.canCopyTargetShareholders) return [];
    const targetShareholderIds = new Set(
      this.targetShareholdersInfo?.shareholders.map((s) => s.id) ?? [],
    );
    return uniq(
      this.shareAllocations
        .map((allocation) => allocation.shareholder.id)
        .filter((id) => targetShareholderIds.has(id)),
    );
  }

  get canRegisterForTransferringCompany(): boolean {
    return (
      this.mergerPartyModel.value === MergerParty.ACQUIROR &&
      this.company !== undefined &&
      !this.mergerAlreadyExistsOnTarget &&
      this.companyData.companiesWithAdminAccess.some(
        (c) => c.organisationNumber === this.company?.organizationnumber,
      )
    );
  }

  prepareData(
    shareholderIdMapping: { fromShareholderId: number; toShareholderId: number }[],
  ): MergerDTO {
    return {
      mergerType: this.mergerType.value,
      acquiror: this.acquiror,
      target: this.target,
      mergerCapitalChanges: this.requiresPaidInCapital
        ? compact(this.mergerCapitalChanges.map((c) => c.prepareData()))
        : [],
      targetShareClassData: this.requiresTargetShareClassData
        ? this.targetShareClassData.map((c) => c.prepareData())
        : [],
      acquirorNominalValue: this.requiresTargetShareClassData
        ? this.acquirorNominalValue.parsedValue
        : undefined,
      targetNominalValue: this.requiresTargetNominalValue
        ? this.targetNominalValue.parsedValue
        : undefined,
      shareAllocation: this.requiresShareAllocation
        ? this.mapShareholders(shareholderIdMapping)
        : [],
      transactionDate: this.transactionDateCardModel.combinedDateAndTime!,
      organizationnumber: this.orgNo,
    };
  }

  private mapShareholders(
    shareholderIdMapping: { fromShareholderId: number; toShareholderId: number }[],
  ) {
    return this.activeAllocations.map((s) => {
      const targetShareTypeCode = this.mergerCapitalChanges.find(
        (c) => c.acquirorShareType.value.sharetypeId === s.shareType.sharetypeId,
      )!.targetShareType?.value;

      const mapping = shareholderIdMapping.find((m) => m.fromShareholderId === s.shareholder.id);
      return s.prepareData(targetShareTypeCode, mapping?.toShareholderId);
    });
  }

  *submit(transactionId?: number) {
    this.submitResult = pending();
    try {
      const shareholderIds = this.targetShareholderIdsToCopy;
      const copyFromOrgNo =
        shareholderIds.length > 0 ? this.company?.organizationnumber : undefined;
      yield this.transactionStore.merger(this, shareholderIds, copyFromOrgNo, transactionId);
      this.submitResult = ok();
    } catch (e) {
      this.submitResult = failure({ key: "shareholders.error.generalError" });
    }
  }

  validateTargetShareholders() {
    this.shareholderError = undefined;
    if (
      (this.mergerType.value === MergerType.PARENT_WHOLLY_OWNED_SUBSIDARY &&
        this.mergerPartyModel.value === MergerParty.TARGET) ||
      (this.mergerType.value === MergerType.REVERSE_PARENT_SUBSIDIARY &&
        this.mergerPartyModel.value === MergerParty.ACQUIROR)
    ) {
      if (this.activeShareholders.length === 1) {
        this.setSearchQuery(
          `${this.activeShareholders[0].name} (${this.activeShareholders[0].companyNumber})`,
        );
        this.selectCompany({
          name: this.activeShareholders[0].name,
          organizationnumber: this.activeShareholders[0].companyNumber ?? "",
        });
      } else if (this.activeShareholders.length > 1) {
        this.shareholderError = {
          key: "merger.subsidiary.multiple-shareholders",
        };
      }
    }
  }

  get requiresTargetOwnedByAcquiror(): boolean {
    return (
      this.mergerType.value === MergerType.PARENT_WHOLLY_OWNED_SUBSIDARY &&
      this.mergerPartyModel.value === MergerParty.ACQUIROR
    );
  }

  *validateTargetOwnedByAcquiror() {
    this.acquirerError = undefined;
    if (!this.requiresTargetOwnedByAcquiror) {
      return;
    }
    const target = this.company;
    const isAdminOnTarget = this.companyData.companiesWithAdminAccess.some(
      (c) => c.organisationNumber === target?.organizationnumber,
    );
    if (!target || !isAdminOnTarget) {
      return;
    }
    const response: ShareholdersInfo = yield this.apiService.fetchShareholdersAll(
      target.organizationnumber,
    );
    if (this.company !== target || !this.requiresTargetOwnedByAcquiror) {
      return;
    }
    const owners = response.shareholders.filter((s) => hasShares(s));
    this.acquirerError =
      owners.length === 1 && owners[0].companyNumber === this.orgNo
        ? undefined
        : {
            key: "merger.subsidiary.not-wholly-owned",
            params: { acquirorName: this.companyName, targetName: target.name },
          };
  }

  get activeShareholders(): ExtendedShareholderDTO[] {
    return this.shareholders.filter((s) => hasShares(s));
  }

  get shareholders() {
    return this.shareholderData.shareholders;
  }

  *refreshMergerExistsOnTarget() {
    if (this.transactionData) {
      return;
    }
    const transactionDate = this.transactionDateCardModel.combinedDateAndTime;
    const targetOrgNo = this.company?.organizationnumber;
    const hasAdminOnTarget =
      targetOrgNo !== undefined &&
      this.companyData.companiesWithAdminAccess.some((c) => c.organisationNumber === targetOrgNo);
    if (!transactionDate || !targetOrgNo || !hasAdminOnTarget) {
      this.mergerAlreadyExistsOnTarget = false;
      return;
    }
    const transactions: Transaction[] = yield this.apiService.fetchTransactionLog(targetOrgNo);
    const transactionDateMs = toDate(transactionDate).getTime();
    this.mergerAlreadyExistsOnTarget = transactions.some(
      (t) =>
        t.transactionType === "MERGER" &&
        toDate(t.transactionDate).getTime() === transactionDateMs &&
        (t.payload as MergerDTO | undefined)?.acquiror?.orgNo === this.orgNo,
    );
  }

  *fetchTargetNominalValue(orgNo: string) {
    const companyInfo: CompanyInfo = yield this.apiService.fetchCompanyInfo(orgNo);
    this.targetNominalValue.setAndCommitInputValue(companyInfo.shareNominalValue);
  }

  *fetchTargetShareholders(orgNo: string, dateTime: string) {
    this.result = pending();

    try {
      const response: ShareholdersInfo = yield this.apiService.fetchShareholdersAll(
        orgNo,
        dateBefore(dateTime),
      );
      this.targetShareholdersInfo = response;
      this.mergerCapitalChanges = [];
      const targetShareTypes = response.shareTypes.filter((st) => !st.historical);
      this.mergerCapitalChanges.push(
        ...this.shareTypes.map(
          (shareType, index) =>
            new MergerCapitalChangeModel(
              this.shareTypes,
              this.mergerCapitalChanges,
              index < targetShareTypes.length ? targetShareTypes[index].shareTypeCode : undefined,
              shareType,
              index < targetShareTypes.length
                ? response.calculatedSharesBySharetypeCode[targetShareTypes[index].shareTypeCode]
                : undefined,
            ),
        ),
      );
      this.initShareAllocations(response);

      this.result = ok();
    } catch (e) {
      this.result = failure();
    }
  }

  private initShareAllocations(response: ShareholdersInfo) {
    const shareholders = response.shareholders.filter(hasShares);

    if (this.mergerCapitalChanges.some((change) => !change.validateShareTypes())) {
      this.shareAllocations = shareholders.flatMap((shareholder) =>
        this.shareTypes.map((shareType) => {
          return new MergerShareAllocationModel(newUuid(), shareholder, shareType);
        }),
      );
      return;
    }
    this.shareAllocations = shareholders.flatMap((shareholder) =>
      this.shareTypes.map((shareType) => {
        const change = this.mergerCapitalChanges.find(
          (c) => c.acquirorShareType.value.sharetypeId === shareType.sharetypeId,
        );

        const sharesToAllocate =
          this.mergerType.value === MergerType.ORDINARY_ACQUISITION
            ? change?.issuedNumberOfShares.parsedValue
            : (this.shareholderData.shareSumBySharetypeCode[shareType.shareTypeCode] ?? 0);

        const prefillNumberOfShares = calculateShareAllocation(
          change?.targetShareType.value,
          shareholder.calculatedShares,
          response.calculatedSharesBySharetypeCode,
          sharesToAllocate,
        );
        return new MergerShareAllocationModel(
          newUuid(),
          shareholder,
          shareType,
          prefillNumberOfShares,
        );
      }),
    );
  }

  clearShareAllocations() {
    this.shareAllocations.forEach((shareAllocation) =>
      shareAllocation.numberOfShares.setInputValue(""),
    );
  }

  recalculateAllocations() {
    const shareholdersInfo = this.targetShareholdersInfo;
    if (shareholdersInfo) {
      this.initShareAllocations(shareholdersInfo);
    }
  }
}

export function calculateShareAllocation(
  targetShareTypeCode: ShareTypeCode | undefined,
  shareholderTargetShares: { [key in ShareTypeCode]?: number },
  targetSharesBySharetypeCode: { [shareTypeCode: string]: number },
  sharesToAllocate?: number,
) {
  if (!targetShareTypeCode || !sharesToAllocate) return undefined;
  const shares = shareholderTargetShares[targetShareTypeCode] ?? 0;
  const ownershipPercentage = shares / targetSharesBySharetypeCode[targetShareTypeCode];
  return round(sharesToAllocate * ownershipPercentage);
}

export class MergerCapitalChangeModel {
  id = newUuid();
  paidInCapitalForTarget: NumberFieldModel;
  paidInPremiumForTarget: NumberFieldModel;
  acquirorShareType: RadioFieldModel<ShareType>;
  targetShareType: RadioFieldModel<ShareTypeCode>;
  targetNumberOfShares: NumberFieldModel;
  issuedNumberOfShares: NumberFieldModel;

  constructor(
    readonly acquirorShareTypes: Array<ShareType>,
    readonly mergerCapitalChanges: Array<MergerCapitalChangeModel>,
    readonly loadedShareTypeCode?: ShareTypeCode,
    readonly prefillAcquirorShareType?: ShareType,
    readonly calculatedNumberOfShares?: number,
    readonly transactionData?: MergerCapitalChange,
  ) {
    this.paidInCapitalForTarget = new NumberFieldModel({
      isPositive: true,
      value: transactionData?.paidInCapitalForTarget,
    });
    this.paidInPremiumForTarget = new NumberFieldModel({
      isPositiveOrZero: true,
      value: transactionData?.paidInPremiumForTarget,
    });
    this.targetNumberOfShares = new NumberFieldModel({
      required: true,
      isPositiveOrZero: true,
      value: transactionData?.targetNumberOfShares ?? this.calculatedNumberOfShares,
    });
    this.issuedNumberOfShares = new NumberFieldModel({
      required: true,
      isPositiveOrZero: true,
      value: transactionData?.issuedNumberOfShares,
    });

    this.acquirorShareType = new RadioFieldModel({
      value: prefillAcquirorShareType ?? acquirorShareTypes[0],
      options: acquirorShareTypes,
      labelFn: () => ({
        translated: "",
      }),
      idFn: (shareType) => shareType.sharetypeId.toString(),
      required: true,
    });

    const shareTypeCodes: Array<ShareTypeCode> = Object.values(ShareTypeCode).filter(
      (s) => s !== ShareTypeCode.UNKNOWN,
    );
    this.targetShareType = new RadioFieldModel({
      value: loadedShareTypeCode ?? shareTypeCodes[0],
      options: shareTypeCodes,
      labelFn: (shareTypeCode) => shareTypeLabel(shareTypeCode),
      idFn: (shareTypeCode) => shareTypeCode,
    });
  }

  validate() {
    this.validateShareTypes();
    this.paidInCapitalForTarget.validate();
    this.paidInPremiumForTarget.validate();
  }

  validateShareTypes() {
    if (this.hasTargetShareType(this.targetShareType.value, this.id)) {
      this.targetShareType.error = { key: "error.share-type-already-used" };
    } else {
      this.targetShareType.error = undefined;
    }
    if (this.hasAcquirorShareType(this.acquirorShareType.value, this.id)) {
      this.acquirorShareType.error = { key: "error.share-type-already-used" };
    } else {
      this.acquirorShareType.error = undefined;
    }
    return this.targetShareType.error === undefined && this.acquirorShareType.error === undefined;
  }

  hasTargetShareType(shareTypeCode: ShareTypeCode, excludingId: string) {
    return this.mergerCapitalChanges.some(
      (change) => change.targetShareType.value === shareTypeCode && change.id !== excludingId,
    );
  }

  hasAcquirorShareType(shareType: ShareType, excludingId: string) {
    return this.mergerCapitalChanges.some(
      (change) => change.acquirorShareType.value === shareType && change.id !== excludingId,
    );
  }

  get isValid() {
    return (
      this.targetShareType.isValid &&
      this.paidInCapitalForTarget.isValid &&
      this.paidInPremiumForTarget.isValid
    );
  }

  prepareData(): MergerCapitalChange | undefined {
    return this.targetShareType.value
      ? {
          targetShareTypeCode: this.targetShareType.value,
          acquirorShareTypeId: this.acquirorShareType.value.sharetypeId,
          paidInCapitalForTarget: this.paidInCapitalForTarget.parsedValue,
          paidInPremiumForTarget: this.paidInPremiumForTarget.parsedValue,
          targetNumberOfShares: this.targetNumberOfShares.parsedValue,
          issuedNumberOfShares: this.issuedNumberOfShares.parsedValue,
        }
      : undefined;
  }
}

export class TargetShareClassDataModel {
  id = newUuid();
  numberOfShares: NumberFieldModel;
  acquirorShareType: RadioFieldModel<ShareTypeCode>;

  constructor(
    readonly targetShareType: ShareType,
    readonly targetShareClassData?: TargetShareClassData,
  ) {
    this.numberOfShares = new NumberFieldModel({
      value: targetShareClassData?.acquirorNumberOfSharesInShareType,
      required: true,
      isPositiveOrZero: true,
    });

    const shareTypeCodes: Array<ShareTypeCode> = Object.values(ShareTypeCode).filter(
      (s) => s !== ShareTypeCode.UNKNOWN,
    );
    this.acquirorShareType = new RadioFieldModel({
      value: targetShareClassData?.acquirorShareTypeCode ?? shareTypeCodes[0],
      options: shareTypeCodes,
      labelFn: (shareTypeCode) => shareTypeLabel(shareTypeCode),
      idFn: (shareTypeCode) => shareTypeCode,
    });
  }

  validate() {
    this.numberOfShares.validate();
  }

  get isValid() {
    return this.acquirorShareType.isValid && this.numberOfShares.isValid;
  }

  prepareData(): TargetShareClassData {
    return {
      targetShareTypeId: this.targetShareType.sharetypeId,
      acquirorShareTypeCode: this.acquirorShareType.value,
      acquirorNumberOfSharesInShareType: this.numberOfShares.parsedValue!,
    };
  }
}

export class MergerShareAllocationModel {
  id: string;
  shareholder: ExtendedShareholderDTO;
  shareType: ShareType;
  numberOfShares: NumberFieldModel;
  sharePrice: NumberFieldModel;
  sharePremium: NumberFieldModel;
  paidInShareCapital: NumberFieldModel;
  bonusShareCapital: NumberFieldModel;

  constructor(
    id: string,
    shareholder: ExtendedShareholderDTO,
    shareType: ShareType,
    prefillNumberOfShares?: number,
    prefill?: {
      sharePrice?: number;
      sharePremium?: number;
      paidInShareCapital?: number;
      bonusShareCapital?: number;
    },
  ) {
    this.id = id;
    this.shareholder = shareholder;
    this.shareType = shareType;
    this.numberOfShares = new NumberFieldModel({
      value: prefillNumberOfShares,
      isPositiveOrZero: true,
    });
    this.sharePrice = new NumberFieldModel({
      value: prefill?.sharePrice,
      isPositiveOrZero: true,
    });
    this.sharePremium = new NumberFieldModel({
      value: prefill?.sharePremium,
      isPositiveOrZero: true,
    });
    this.paidInShareCapital = new NumberFieldModel({
      value: prefill?.paidInShareCapital,
      isPositiveOrZero: true,
    });
    this.bonusShareCapital = new NumberFieldModel({
      value: prefill?.bonusShareCapital,
      isPositiveOrZero: true,
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }

  prepareData(targetShareTypeCode: ShareTypeCode, toShareholderId?: number): MergerShareAllocation {
    return {
      shareholderId: toShareholderId ?? this.shareholder.id,
      shareTypeId: this.shareType.sharetypeId,
      numberOfShares: this.numberOfShares.toNumberOrZero(),
      targetShareTypeCode: targetShareTypeCode,
      sharePrice: this.sharePrice.toNumber(),
      sharePremium: this.sharePremium.toNumber(),
      paidInShareCapital: this.paidInShareCapital.toNumber(),
      bonusShareCapital: this.bonusShareCapital.toNumber(),
    };
  }
}

export function isActorExistingShareholder(
  actor: SelectedActor,
  shareholder: ExtendedShareholderDTO,
) {
  return (
    actor.reference === toShareholderReference(shareholder.id) ||
    actor.inCompany?.shareholderId === shareholder.id ||
    (actor.companyNumber !== undefined && actor.companyNumber === shareholder.companyNumber)
  );
}
